import axios from "axios";
import { getAuthUserDetails } from "./Helpers";
import USOBApiRequest from "./ApiUrls";
const LosslessJSON = require("lossless-json");

const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

let cachedToken = null;
let tokenExpiresAt = 0;
let tokenFetchPromise = null;

function isTokenRequired(apiName) {
  if (!apiName || typeof apiName !== "string") return false;
  if (apiName.startsWith("/dashboard/vehicle-data")) return true;
  if (apiName.startsWith("/dealer-locator")) return true;
  if (apiName.startsWith("/dashboard/summary")) return true;
  if (apiName === "/segment" || apiName.startsWith("/segment?")) return true;
  if (apiName.startsWith("/groups?page=")) return true;
  if (apiName.startsWith("/news")) return true;
  return false;
}

async function fetchUSOBAccessToken() {
  const params = new URLSearchParams();
  params.append("grant_type", "client_credentials");
  params.append("client_id", process.env.REACT_APP_USOB_TOKEN_CLIENT_ID);
  params.append("client_secret", process.env.REACT_APP_USOB_TOKEN_CLIENT_SECRET);

  const response = await axios.post(
    process.env.REACT_APP_USOB_TOKEN_URL,
    params.toString(),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

async function getUSOBAccessToken() {
  const now = Date.now();
  if (cachedToken && tokenExpiresAt > now + TOKEN_REFRESH_BUFFER_MS) {
    return cachedToken;
  }
  if (tokenFetchPromise) {
    return tokenFetchPromise;
  }
  tokenFetchPromise = fetchUSOBAccessToken()
    .then((data) => {
      cachedToken = data.access_token;
      const expiresIn = (data.expires_in || 86400) * 1000;
      tokenExpiresAt = Date.now() + expiresIn;
      tokenFetchPromise = null;
      return cachedToken;
    })
    .catch((err) => {
      tokenFetchPromise = null;
      throw err;
    });
  return tokenFetchPromise;
}

async function buildHeaders(apiName, contentType, header = {}) {
  const baseHeaders = {
    "Content-Type": contentType,
    userid: getAuthUserDetails()?.usob_id,
    customerid: getAuthUserDetails()?.usob_cid,
    roleid: getAuthUserDetails()?.usob_rid,
    ...header,
  };
  if (isTokenRequired(apiName)) {
    const token = await getUSOBAccessToken();
    baseHeaders.Authorization = `Bearer ${token}`;
  }
  return baseHeaders;
}

// convert Decimal to LosslessNumber
function replacer(key, value) {
  if (value && value.isLosslessNumber) {
    if (value.value.length > 15) {
      return `${value}`;
    } else {
      return parseInt(value);
    }
  } else {
    return value;
  }
}
class Service {
  constructor() {
    this.get = async (apiName, header = {}, cancelToken, config = {}) => {
      const headers = await buildHeaders(apiName, "text/plain", header);
      return axios.get(USOBApiRequest.USOBApiBaseURL + apiName, {
        headers,
        cancelToken,
        ...config,
        transformResponse: (x) => {
          const cc = LosslessJSON.parse(x, replacer);
          return cc;
        },
      });
    };

    this.post = (apiName, postData, header = {}, cancelToken, config = {}) => {
      return axios.post(
        USOBApiRequest.USOBApiBaseURL + apiName,
        { ...postData },
        {
          headers: {
            "Content-Type": "application/json",
            userid: getAuthUserDetails()?.usob_id,
            customerid: getAuthUserDetails()?.usob_cid,
            roleid: getAuthUserDetails()?.usob_rid,
            ...header,
          },
          cancelToken,
          ...config,
        }
      );
    };

    this.put = (apiName, postData, header = {}, config = {}) => {
      return axios.put(USOBApiRequest.USOBApiBaseURL + apiName, postData, {
        headers: {
          "Content-Type": "application/json",
          userid: getAuthUserDetails()?.usob_id,
          customerid: getAuthUserDetails()?.usob_cid,
          roleid: getAuthUserDetails()?.usob_rid,
          ...header,
        },
        ...config,
      });
    };

    this.delete = (apiName, header = {}, config = {}) => {
      return axios.delete(USOBApiRequest.USOBApiBaseURL + apiName, {
        headers: {
          "Content-Type": "application/json",
          userid: getAuthUserDetails()?.usob_id,
          customerid: getAuthUserDetails()?.usob_cid,
          roleid: getAuthUserDetails()?.usob_rid,
          ...header,
        },
        ...config,
      });
    };
  }
}
// eslint-disable-next-line
export default new Service();
